/**
 * NodeToolEmbed — engine-aware tool switcher for the Tool view.
 *
 * google_sheets → Split layout: left info panel (w-56) + right sheet iframe with tab switcher.
 *                 Tab buttons navigate via #gid= fragment (requires input_gid / output_gid in metadata).
 * python → PythonNodeView
 * jupyter → placeholder
 * No engine → description + schema display
 *
 * Fine-grained Zustand selectors to prevent iframe re-renders.
 */

import React, { memo, useState, useEffect } from 'react';
import api from '../../apiService';
import { useProjectStore } from '../../stores/projectStore';
import { useProcessViewerStore } from '../../stores/processViewerStore';
import RawIframe from './RawIframe';
import PythonNodeView from './PythonNodeView';
import DataPreview from './DataPreview';
import { projectNotebookUrl } from './projectNotebookUrl';
import type { NodeDefinition, ModelRun, SheetsNodeMetadata } from '../../types/modeling';
import { isSheetsNode, isPythonNode, isJupyterNode, buildSheetEmbedUrl, getNodeTypeColor } from '../../types/modeling';

interface NodeToolEmbedProps {
  node: NodeDefinition;
  lastRun: ModelRun | null;
  activeRunProgress: ModelRun | null;
  googleWorkspaceEmail?: string | null;
  authToken?: string | null;
  artifactOpenTarget?: ArtifactOpenTarget | null;
}

interface ArtifactOpenTarget {
  artifactName: string;
  artifactPath: string;
  /** True = artifactPath IS the folder to open (don't strip a trailing
   *  filename). INPUT_DATA nodes use this to open their data folder directly. */
  isFolder?: boolean;
}

// ---------------------------------------------------------------------------
// Google Sheets — split layout
// ---------------------------------------------------------------------------

function SheetsNodeEmbed({ node, googleWorkspaceEmail }: { node: NodeDefinition & { metadata: SheetsNodeMetadata }; googleWorkspaceEmail?: string | null }) {
  const metadata = node.metadata as SheetsNodeMetadata & Record<string, any>;
  const spreadsheetUrl = metadata?.spreadsheet_url;
  const inputSheet = metadata?.input_sheet;
  const outputSheet = metadata?.output_sheet;
  const inputGid = metadata?.input_gid;
  const outputGid = metadata?.output_gid;

  // Default to 'input' tab if available, else 'output', else null (show base URL)
  const hasInput = Boolean(inputSheet);
  const hasOutput = Boolean(outputSheet);
  const [activeTab, setActiveTab] = useState<'input' | 'output'>(hasInput ? 'input' : 'output');
  const [infoPanelOpen, setInfoPanelOpen] = useState(true);

  if (!spreadsheetUrl) {
    return (
      <NoToolPlaceholder
        message="No spreadsheet URL configured for this node."
        description={node.description}
      />
    );
  }

  const activeGid = activeTab === 'input' ? inputGid : outputGid;
  const embedUrl = buildSheetEmbedUrl(spreadsheetUrl, activeGid);
  const color = getNodeTypeColor(node.node_type);

  return (
    <div className="h-full flex overflow-hidden">
      {/* Left: Node info panel (collapsible) */}
      {infoPanelOpen ? (
        <div className="w-56 flex-shrink-0 border-r border-border-default bg-bg-warm flex flex-col overflow-hidden">
          {/* Node type + name header with collapse button */}
          <div className="px-3 pt-3 pb-2 border-b border-border-default flex-shrink-0 flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white mb-1.5"
                style={{ backgroundColor: color }}
              >
                {node.node_type.replace('_', ' ')}
              </div>
              <div className="text-sm font-semibold text-fg leading-snug">{node.name}</div>
            </div>
            <button
              onClick={() => setInfoPanelOpen(false)}
              className="p-0.5 rounded hover:bg-bg-warm transition-colors flex-shrink-0 mt-0.5"
              title="Collapse node info"
            >
              <svg className="w-3.5 h-3.5 text-fg-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          </div>

          {/* Description */}
          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-3 text-xs">
            {node.description && (
              <div>
                <div className="font-semibold text-fg-tertiary uppercase tracking-wide text-[9px] mb-1">Description</div>
                <p className="text-fg-secondary leading-relaxed">{node.description}</p>
              </div>
            )}

            {node.logic_steps && node.logic_steps.length > 0 && (
              <div>
                <div className="font-semibold text-fg-tertiary uppercase tracking-wide text-[9px] mb-1">Logic steps</div>
                <ol className="text-fg-secondary list-decimal list-inside space-y-1 leading-relaxed">
                  {node.logic_steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
            )}

            {node.logic_description && !node.logic_steps?.length && (
              <div>
                <div className="font-semibold text-fg-tertiary uppercase tracking-wide text-[9px] mb-1">Logic</div>
                <p className="text-fg-secondary leading-relaxed">{node.logic_description}</p>
              </div>
            )}

            {(inputSheet || outputSheet) && (
              <div>
                <div className="font-semibold text-fg-tertiary uppercase tracking-wide text-[9px] mb-1">Sheets</div>
                <div className="space-y-1">
                  {inputSheet && (
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-success flex-shrink-0" />
                      <span className="text-fg-tertiary">In:</span>
                      <span className="text-fg-secondary font-medium truncate">{inputSheet}</span>
                    </div>
                  )}
                  {outputSheet && (
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                      <span className="text-fg-tertiary">Out:</span>
                      <span className="text-fg-secondary font-medium truncate">{outputSheet}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Collapsed info tab */
        <div className="flex-shrink-0 flex items-center">
          <button
            onClick={() => setInfoPanelOpen(true)}
            title="Expand node info"
            className="flex flex-col items-center gap-1.5 w-7 h-full border-r border-border-default bg-bg-warm hover:bg-bg-warm transition-colors py-4"
            style={{ borderRightColor: color }}
          >
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: color }}
            />
            <span className="text-[9px] font-semibold text-fg-tertiary [writing-mode:vertical-rl] rotate-180 tracking-wide truncate max-h-24">
              {node.name.length > 14 ? node.name.slice(0, 12) + '…' : node.name}
            </span>
          </button>
        </div>
      )}

      {/* Right: Sheet iframe with tab strip */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Tab strip — only show if both tabs exist */}
        {hasInput && hasOutput && (
          <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border-default bg-surface flex-shrink-0">
            <button
              onClick={() => setActiveTab('input')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                activeTab === 'input'
                  ? 'bg-success-bg text-success border border-success'
                  : 'text-fg-tertiary hover:bg-bg-warm'
              }`}
            >
              {inputSheet ?? 'Input'}
            </button>
            <button
              onClick={() => setActiveTab('output')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                activeTab === 'output'
                  ? 'bg-accent-tint text-accent-hover border border-accent-border'
                  : 'text-fg-tertiary hover:bg-bg-warm'
              }`}
            >
              {outputSheet ?? 'Output'}
            </button>
            {!activeGid && (
              <span className="ml-2 text-[10px] text-fg-tertiary italic">
                (set gid to enable tab navigation)
              </span>
            )}
          </div>
        )}

        <RawIframe
          key={`${spreadsheetUrl}-${activeTab}-${activeGid ?? 'nogid'}`}
          url={embedUrl}
          title={`${node.name} — ${activeTab === 'input' ? inputSheet ?? 'Input' : outputSheet ?? 'Output'}`}
          googleWorkspaceEmail={googleWorkspaceEmail}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Jupyter Notebook — split layout (same pattern as Sheets)
// ---------------------------------------------------------------------------

function artifactFolderRef(artifactPath: string): string {
  const parts = artifactPath.split('/').filter(Boolean);
  // Drop the filename; a single-segment ref means the tenant root (no subfolder)
  // — returning the bare filename here would point the tree at a file-as-dir.
  return parts.length <= 1 ? '' : parts.slice(0, parts.length - 1).join('/');
}

function encodeJupyterPath(path: string): string {
  return path.split('/').filter(Boolean).map(encodeURIComponent).join('/');
}

function buildJupyterTreeUrl(
  serverUrl: string,
  initialPath: string,
  artifactPath: string,
  isFolder = false,
): string {
  const root = serverUrl.replace(/\/+$/, '');
  // Normalise the server-provided base path to exactly one leading slash and no
  // trailing slash — robust to whatever format the token endpoint returns (the
  // Jupyter folder-naming scheme can change; see the G6 naming work).
  const base = '/' + (initialPath || '').replace(/^\/+|\/+$/g, '');
  // isFolder: the path already IS the folder (INPUT_DATA data dir) — open it
  // directly. Otherwise it's a file path; drop the filename to get its folder.
  const folder = encodeJupyterPath(
    isFolder ? artifactPath.replace(/^\/+|\/+$/g, '') : artifactFolderRef(artifactPath),
  );
  return folder ? `${root}${base}/${folder}` : `${root}${base}`;
}

function withJupyterToken(url: string, paramName: string, token: string, anchor?: string): string {
  const hashIndex = url.indexOf('#');
  const base = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
  const existingHash = hashIndex >= 0 ? url.slice(hashIndex) : '';
  const sep = base.includes('?') ? '&' : '?';
  const hash = anchor ? `#${anchor}` : existingHash;
  return `${base}${sep}${paramName}=${encodeURIComponent(token)}${hash}`;
}

function JupyterNodeEmbed({ node, authToken, artifactOpenTarget }: {
  node: NodeDefinition;
  authToken?: string | null;
  artifactOpenTarget?: ArtifactOpenTarget | null;
}) {
  const metadata = node.metadata as Record<string, any>;
  // INPUT_DATA nodes are data, not code — they have no notebook to open (and any
  // notebook_url on one is a stale/malformed artifact, owner #1). Suppress the
  // notebook launch target entirely; such a node only ever opens its DATA folder
  // via an artifactOpenTarget.
  const isInputDataNode = node.node_type === 'input_data';
  const notebookUrl = isInputDataNode ? null : (metadata?.notebook_url ?? null);
  const [infoPanelOpen, setInfoPanelOpen] = useState(true);
  const [jupyterToken, setJupyterToken] = useState<string | null>(null);
  const [jupyterServerUrl, setJupyterServerUrl] = useState<string | null>(null);
  const [jupyterInitialPath, setJupyterInitialPath] = useState<string | null>(null);
  const color = getNodeTypeColor(node.node_type);
  const projectId = useProjectStore(s => s.activeProject?.project_id);

  // Fetch Jupyter token once (requires Firebase auth). The response
  // carries the scoped JWT as ``iframe_token`` in production and falls
  // back to the legacy ``jupyter_token`` when iframe-JWT keys aren't
  // configured (dev bootstrap). Either goes into the URL query param.
  const [isLegacyToken, setIsLegacyToken] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  useEffect(() => {
    if (!projectId || !authToken || (!notebookUrl && !artifactOpenTarget?.artifactPath)) return;
    setTokenError(false);
    api.getJupyterToken(projectId, authToken)
      .then(data => {
        const token = data.iframe_token || data.jupyter_token || null;
        setJupyterToken(token);
        setJupyterServerUrl(data.server_url || null);
        setJupyterInitialPath(data.initial_path || null);
        setIsLegacyToken(!data.iframe_token && !!data.jupyter_token);
        if (!token) setTokenError(true);
      })
      .catch(err => {
        console.warn('[Jupyter] Failed to get token:', err);
        setTokenError(true);
      });
  }, [projectId, authToken, notebookUrl, artifactOpenTarget?.artifactPath]);

  // Build URL with token + anchor to scroll to this node's markdown header,
  // or to the produced artifact's folder when the inspector requested one.
  const nodeAnchor = node.name ? encodeURIComponent(node.name) : '';
  const paramName = isLegacyToken ? 'token' : 'bark_token';
  const artifactTreeUrl = artifactOpenTarget?.artifactPath && jupyterServerUrl && jupyterInitialPath
    ? buildJupyterTreeUrl(jupyterServerUrl, jupyterInitialPath, artifactOpenTarget.artifactPath, artifactOpenTarget.isFolder)
    : null;
  // The stored link can point outside this project (a copy's source, or the old
  // <company>/notebooks/ layout), which Jupyter refuses. Open the same file in
  // this project's folder instead.
  const projectNotebookTarget = notebookUrl && jupyterServerUrl && jupyterInitialPath
    ? projectNotebookUrl(notebookUrl, jupyterServerUrl, jupyterInitialPath)
    : notebookUrl;
  const targetUrl = artifactTreeUrl || projectNotebookTarget || null;
  const authedUrl = targetUrl && jupyterToken
    ? withJupyterToken(targetUrl, paramName, jupyterToken, artifactTreeUrl ? undefined : nodeAnchor)
    : null;
  const hasLaunchTarget = Boolean(notebookUrl || artifactOpenTarget?.artifactPath);

  return (
    <div className="h-full flex overflow-hidden">
      {/* Left: Node info panel (collapsible) */}
      {infoPanelOpen ? (
        <div className="w-56 flex-shrink-0 border-r border-border-default bg-bg-warm flex flex-col overflow-hidden">
          <div className="px-3 pt-3 pb-2 border-b border-border-default flex-shrink-0 flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide text-white mb-1.5"
                style={{ backgroundColor: color }}
              >
                {node.node_type.replace('_', ' ')}
              </div>
              <div className="text-sm font-semibold text-fg leading-snug">{node.name}</div>
            </div>
            <button
              onClick={() => setInfoPanelOpen(false)}
              className="p-0.5 rounded hover:bg-bg-warm transition-colors flex-shrink-0 mt-0.5"
              title="Collapse node info"
            >
              <svg className="w-3.5 h-3.5 text-fg-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-3 text-xs">
            {node.description && (
              <div>
                <div className="font-semibold text-fg-tertiary uppercase tracking-wide text-[9px] mb-1">Description</div>
                <p className="text-fg-secondary leading-relaxed">{node.description}</p>
              </div>
            )}

            {node.logic_steps && node.logic_steps.length > 0 && (
              <div>
                <div className="font-semibold text-fg-tertiary uppercase tracking-wide text-[9px] mb-1">Logic steps</div>
                <ol className="text-fg-secondary list-decimal list-inside space-y-1 leading-relaxed">
                  {node.logic_steps.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </div>
            )}

            {node.logic_description && !node.logic_steps?.length && (
              <div>
                <div className="font-semibold text-fg-tertiary uppercase tracking-wide text-[9px] mb-1">Logic</div>
                <p className="text-fg-secondary leading-relaxed">{node.logic_description}</p>
              </div>
            )}

            <div>
              <div className="font-semibold text-fg-tertiary uppercase tracking-wide text-[9px] mb-1">Engine</div>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                <span className="text-fg-secondary font-medium">Jupyter Notebook</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex-shrink-0 flex items-center">
          <button
            onClick={() => setInfoPanelOpen(true)}
            title="Expand node info"
            className="flex flex-col items-center gap-1.5 w-7 h-full border-r border-border-default bg-bg-warm hover:bg-bg-warm transition-colors py-4"
            style={{ borderRightColor: color }}
          >
            <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
            <span className="text-[9px] font-semibold text-fg-tertiary [writing-mode:vertical-rl] rotate-180 tracking-wide truncate max-h-24">
              {node.name.length > 14 ? node.name.slice(0, 12) + '…' : node.name}
            </span>
          </button>
        </div>
      )}

      {/* Right: Notebook iframe or placeholder */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {artifactOpenTarget && (
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border-default bg-accent-tint flex-shrink-0">
            <span className="font-body text-[10px] font-bold uppercase tracking-[0.08em] text-accent-hover flex-shrink-0">
              Output
            </span>
            <span className="font-mono text-[11px] font-semibold text-fg flex-shrink-0">
              {artifactOpenTarget.artifactName}
            </span>
            <span className="font-mono text-[10px] text-fg-tertiary truncate" title={artifactOpenTarget.artifactPath}>
              {artifactOpenTarget.artifactPath}
            </span>
          </div>
        )}
        {authedUrl ? (
          <RawIframe
            key={authedUrl}
            url={authedUrl}
            title={`${node.name} — ${artifactOpenTarget ? 'Output artifact' : 'Jupyter Notebook'}`}
          />
        ) : hasLaunchTarget && !tokenError ? (
          /* Notebook exists; token still being fetched. Show an explicit
             loading state so "Open node" doesn't look like a dead click while
             the scoped JWT round-trips and the server warms up. */
          <div className="h-full flex items-center justify-center bg-bg-warm" role="status" aria-live="polite">
            <div className="text-center">
              <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p className="text-xs text-fg-tertiary">
                {artifactOpenTarget ? 'Opening artifact folder…' : 'Launching notebook…'}
              </p>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center bg-bg-warm">
            <div className="text-center max-w-sm">
              <div className="w-12 h-12 rounded-full bg-accent-tint flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-accent-hover" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-fg-secondary mb-1">
                {isInputDataNode ? 'Uploaded data' : 'Jupyter Notebook'}
              </h3>
              <p className="text-xs text-fg-tertiary">
                {tokenError
                  ? 'Could not open the session. Try reopening the node.'
                  : isInputDataNode
                    ? 'Upload a file in the Upload data tab to inspect it here.'
                    : 'The notebook will be available when the agent finishes building it.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function NodeToolEmbedComponent({ node, lastRun, activeRunProgress, googleWorkspaceEmail, authToken, artifactOpenTarget }: NodeToolEmbedProps) {
  const modelId = useProcessViewerStore((s) => s.model?.model_id);
  const connections = useProcessViewerStore((s) => s.model?.connections);

  // INPUT_DATA is data, not code. Opening it shows the uploaded file as an
  // IN-APP TABLE (a parquet is binary — Jupyter's text editor renders it as
  // "not UTF-8 encoded", owner #1). The Jupyter data folder stays one click
  // away via DataPreview's renderJupyterView. We derive the folder target when
  // no explicit one was passed (e.g. double-click / Enter) so the fallback
  // always resolves.
  if (node.node_type === 'input_data') {
    const folderTarget: ArtifactOpenTarget | null =
      artifactOpenTarget ??
      (modelId
        ? {
            artifactName: node.outputs?.[0]?.artifact?.artifact_name ?? '',
            artifactPath: `artifacts/${modelId}/${node.node_id}/current/`,
            isFolder: true,
          }
        : null);
    return (
      <DataPreview
        node={node}
        artifactName={artifactOpenTarget?.artifactName}
        authToken={authToken}
        renderJupyterView={
          folderTarget
            ? () => (
                <JupyterNodeEmbed
                  node={node}
                  authToken={authToken}
                  artifactOpenTarget={folderTarget}
                />
              )
            : undefined
        }
      />
    );
  }

  // OUTPUT node: terminal, no engine, writes nothing of its own — its data
  // lives under the PRODUCER node that feeds it. Resolve the producer + artifact
  // from the model connections and preview THAT, while presenting the output
  // node (owner #3). Falls back to the empty state if no producer is wired yet.
  if (node.node_type === 'output') {
    const upstream = (connections || []).find((c) => c.to_node_id === node.node_id);
    const sourceNodeId = upstream?.from_node_id;
    const producedArtifact = upstream?.artifact_name;
    const folderTarget: ArtifactOpenTarget | null =
      modelId && sourceNodeId
        ? {
            artifactName: producedArtifact ?? '',
            artifactPath: `artifacts/${modelId}/${sourceNodeId}/current/`,
            isFolder: true,
          }
        : null;
    return (
      <DataPreview
        node={node}
        sourceNodeId={sourceNodeId}
        artifactName={producedArtifact}
        authToken={authToken}
        renderJupyterView={
          folderTarget
            ? () => (
                <JupyterNodeEmbed
                  node={node}
                  authToken={authToken}
                  artifactOpenTarget={folderTarget}
                />
              )
            : undefined
        }
      />
    );
  }

  // An explicit "open this artifact" request (clicking an output row on any
  // compute node) shows the produced data as an IN-APP TABLE — a parquet can't
  // render in Jupyter's text editor (owner #1/#3). The Jupyter folder stays one
  // click away via the fallback.
  if (artifactOpenTarget) {
    return (
      <DataPreview
        node={node}
        artifactName={artifactOpenTarget.artifactName}
        authToken={authToken}
        renderJupyterView={() => (
          <JupyterNodeEmbed node={node} authToken={authToken} artifactOpenTarget={artifactOpenTarget} />
        )}
      />
    );
  }

  if (isSheetsNode(node)) {
    return <SheetsNodeEmbed node={node} googleWorkspaceEmail={googleWorkspaceEmail} />;
  }

  if (isPythonNode(node)) {
    return (
      <PythonNodeView
        node={node}
        lastRun={lastRun}
        activeRunProgress={activeRunProgress}
      />
    );
  }

  if (isJupyterNode(node)) {
    return <JupyterNodeEmbed node={node} authToken={authToken} artifactOpenTarget={artifactOpenTarget} />;
  }

  return (
    <NoToolPlaceholder
      message={`This ${node.node_type} node has no computation engine.`}
      description={node.description}
      logicSteps={node.logic_steps}
    />
  );
}

function NoToolPlaceholder({
  message,
  description,
  logicSteps,
}: {
  message: string;
  description?: string;
  logicSteps?: string[];
}) {
  return (
    <div className="h-full flex items-center justify-center bg-bg-warm p-8">
      <div className="max-w-md text-center">
        <p className="text-sm text-fg-tertiary mb-3">{message}</p>
        {description && (
          <p className="text-xs text-fg-tertiary mb-4">{description}</p>
        )}
        {logicSteps && logicSteps.length > 0 && (
          <div className="text-left bg-surface rounded-lg border border-border-default p-4">
            <div className="text-xs font-semibold text-fg-tertiary mb-2">Logic steps:</div>
            <ol className="text-xs text-fg-secondary list-decimal list-inside space-y-1">
              {logicSteps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}

export default memo(NodeToolEmbedComponent);
